import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, Mail, Phone, Linkedin, Package, Video, CalendarDays } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill, inputClass } from "../components/Bits";
import { HarvestShell, useHarvestHeader } from "./HarvestShell";
import { formatLong, stepDate } from "../../lib/harvestDates";

const CHANNEL_ICONS = {
  email: Mail, linkedin: Linkedin, call: Phone, mail: Package, video: Video, event: CalendarDays,
};
const CHANNELS = ["email", "linkedin", "call", "mail", "video", "event"];
const OWNERS = [
  ["auto", "Automated"], ["approval", "Approval gate"], ["kyle", "Kyle manual"],
];
const OWNER_TONE = { auto: "sage", approval: "gold", kyle: "terra" };
const TABS = [["all", "All"], ["t1", "Tier 1"], ["t2", "Tier 2"], ["t3", "Tier 3"], ["rel", "Partners"]];

export default function AdminHarvestCadences() {
  const { settings, pending } = useHarvestHeader();
  const [data, setData] = useState({ cadences: [], steps: [] });
  const [tab, setTab] = useState("all");
  const [channel, setChannel] = useState("");
  const [owner, setOwner] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [enroll, setEnroll] = useState("2026-11-16");

  const load = () => adminApi.get("/crm/harvest/cadences")
    .then(({ data: body }) => setData(body))
    .catch((error) => toast.error(apiError(error)));

  useEffect(() => { load(); }, []);

  const pauses = settings?.pauses || [];
  const bookingUrl = settings?.booking_url || "";

  const cadences = useMemo(
    () => data.cadences.filter((c) => tab === "all" || c.key === tab),
    [data.cadences, tab]
  );

  const stepsFor = (key) => data.steps
    .filter((s) => s.cadence_key === key)
    .filter((s) => !channel || s.channel === channel)
    .filter((s) => !owner || s.owner === owner)
    .filter((s) => !flaggedOnly || s.flag)
    .sort((a, b) => a.day_offset - b.day_offset);

  const saveCadence = async (key, patch) => {
    try {
      await adminApi.patch(`/crm/harvest/cadences/${key}`, patch);
      toast.success("Cadence updated");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const copyBooking = async () => {
    await navigator.clipboard.writeText(bookingUrl);
    toast.success("Booking link copied");
  };

  const totalSteps = data.steps.length;

  return (
    <HarvestShell settings={settings} pending={pending}>
      <Panel
        title={`Cadences · ${totalSteps} steps seeded`}
        testid="harvest-cadences-panel"
        action={
          <div className="flex items-center gap-2">
            <a
              href={bookingUrl || "#"}
              target="_blank"
              rel="noreferrer"
              data-testid="harvest-booking-link"
              className="rounded-full border border-gold/40 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-200 hover:bg-gold/10"
            >
              Book a 15-minute review
            </a>
            <button
              type="button"
              onClick={copyBooking}
              data-testid="harvest-booking-copy"
              className="rounded-full border border-cream/15 p-1.5 text-cream/50 transition-colors duration-200 hover:text-cream"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`harvest-cadence-tab-${key}`}
              className={`rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                tab === key ? "bg-cream/10 text-cream" : "text-cream/45 hover:text-cream"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Channel</span>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              data-testid="harvest-filter-channel"
              className={`${inputClass} mt-1.5`}
            >
              <option value="">All channels</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">Owner</span>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              data-testid="harvest-filter-owner"
              className={`${inputClass} mt-1.5`}
            >
              <option value="">All owners</option>
              {OWNERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
              Batch enroll date
            </span>
            <input
              type="date"
              value={enroll}
              onChange={(e) => setEnroll(e.target.value)}
              data-testid="harvest-enroll-date"
              className={`${inputClass} mt-1.5`}
            />
          </label>
          <label className="flex items-end gap-2 pb-2">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
              data-testid="harvest-filter-flagged"
              className="h-4 w-4 accent-[#C8A24A]"
            />
            <span className="text-xs text-cream/60">Only steps with a check</span>
          </label>
        </div>
      </Panel>

      {cadences.map((cadence) => {
        const steps = stepsFor(cadence.key);
        return (
          <Panel key={cadence.key} testid={`harvest-cadence-${cadence.key}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl text-cream">{cadence.name}</h3>
                <p className="mt-1.5 text-sm text-cream/55">{cadence.audience}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                  {cadence.run_window} · {steps.length} steps shown
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                    Apollo sequence id
                  </span>
                  <input
                    defaultValue={cadence.apollo_sequence_id || ""}
                    onBlur={(e) =>
                      e.target.value !== (cadence.apollo_sequence_id || "")
                      && saveCadence(cadence.key, { apollo_sequence_id: e.target.value })}
                    placeholder="seq_…"
                    data-testid={`harvest-apollo-id-${cadence.key}`}
                    className={`${inputClass} mt-1.5 w-44`}
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                    Status
                  </span>
                  <select
                    value={cadence.status}
                    onChange={(e) => saveCadence(cadence.key, { status: e.target.value })}
                    data-testid={`harvest-status-${cadence.key}`}
                    className={`${inputClass} mt-1.5 w-36`}
                  >
                    {["planned", "draft", "active", "paused", "archived"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {steps.map((step) => {
                const Icon = CHANNEL_ICONS[step.channel] || Mail;
                const computed = stepDate(enroll, step, pauses);
                return (
                  <div
                    key={step.id}
                    data-testid={`harvest-step-card-${step.id}`}
                    className="rounded-lg border border-cream/10 bg-cream/[0.03] p-4"
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-[11px] text-gold">#{step.n}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                        Day {step.day_offset} · {formatLong(computed.date)}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-cream/60">
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.6} />
                        {step.channel}
                      </span>
                      <Pill tone={OWNER_TONE[step.owner]}>
                        {OWNERS.find(([v]) => v === step.owner)?.[1]}
                      </Pill>
                      <Pill>{step.phase}</Pill>
                      {computed.shifted ? (
                        <span
                          data-testid={`harvest-step-shifted-${step.id}`}
                          className="font-mono text-[10px] uppercase tracking-[0.18em] text-sage"
                        >
                          moved from {computed.raw.toISOString().slice(0, 10)}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-3 text-sm text-cream">{step.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-cream/55">{step.copy_summary}</p>
                    {step.trigger ? (
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/35">
                        Trigger · {step.trigger}
                      </p>
                    ) : null}
                    {computed.issues.length ? (
                      <p className="mt-2 text-xs text-sage/80" data-testid={`harvest-step-note-${step.id}`}>
                        Calendar-day note: {computed.issues.join("; ")} — business days move it to{" "}
                        {formatLong(computed.date)}.
                      </p>
                    ) : null}
                    {step.flag ? (
                      <p
                        data-testid={`harvest-step-flag-${step.id}`}
                        className="mt-3 flex items-start gap-2 rounded-md border border-gold/30 bg-gold/5 px-3 py-2 text-xs text-gold"
                      >
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
                        Check: {step.flag}
                      </p>
                    ) : null}
                  </div>
                );
              })}
              {!steps.length ? (
                <p className="text-sm text-cream/40">No steps match these filters.</p>
              ) : null}
            </div>
          </Panel>
        );
      })}
    </HarvestShell>
  );
}
